import { useWindowSize } from 'hooks/useWindowSize';
import * as React from 'react';
import styled from 'styled-components';

/**
 * @member stepNumber step number. Like an array, if there are two items, the stepNumber should be initialized to 2
 * @member activeStep active step of the stepper. Starts at 0.
 * @member src image source
 * @member alt image alternative text
 */
export type JRCImageStepperProps = {
    stepNumber: number;
    activeStep: number;
    src: string;
    alt: string;
};

const Wrapper = styled.div`
    position: relative;
    width: 32%;
    max-width: 480px;
    display: none;
    overflow: hidden;

    @media screen and (min-width: 1280px) {
        & {
            display: block;
        }
    }
`;

const Image = styled.img<{ top: number | undefined }>`
    position: absolute;
    width: 100%;
    ${(props) => (props.top !== undefined ? `top: ${props.top}px` : `top: 0px`)};
    transition: top 1s;
`;

export function JRCImageStepper(props: JRCImageStepperProps) {
    const image = React.useRef<HTMLImageElement>(null);
    const wrapper = React.useRef<HTMLDivElement>(null);

    const [imageHeight, setImageHeight] = React.useState<number | undefined>(undefined);
    const [wrapperHeight, setWrapperHeight] = React.useState<number | undefined>(undefined);
    const [top, setTop] = React.useState<number | undefined>(undefined);
    const windowSize = useWindowSize();

    React.useLayoutEffect(() => {
        setWrapperHeight(wrapper?.current?.getBoundingClientRect().height);
    }, [setWrapperHeight, windowSize, props.activeStep]);

    React.useLayoutEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setTop(
                imageHeight !== undefined && wrapperHeight !== undefined
                    ? (-(imageHeight - wrapperHeight) * props.activeStep) / (props.stepNumber - 1)
                    : undefined,
            );
        }, 25);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [wrapperHeight, imageHeight, props.activeStep, props.stepNumber]);

    return (
        <Wrapper ref={wrapper}>
            <Image
                ref={image}
                src={props.src}
                alt={props.alt}
                top={top}
                onLoad={() => setImageHeight(image?.current?.getBoundingClientRect().height)}
            />
        </Wrapper>
    );
}
